/* eslint-disable @typescript-eslint/no-use-before-define */
import { queryParams } from 'js/sign-components/common/fetch';
import hsFetch, { getCSRFToken } from 'js/sign-components/common/hs-fetch';
import {
  validateWebAppAccount,
  validateWebAppAccountSettings,
} from 'js/sign-components/generated/validators/validateWebApp';
import {
  EditableIntegration,
  WebAppAccount,
  WebAppAccountSettings,
} from 'js/sign-components/generated/types/WebApp';
import {
  assertBasicSuccess,
  assertDataSchemaResponse,
} from 'common/utils/response-helpers';

export type ManagedContact = {
  id: string;
  contactId: string;
  name: string;
  type: 'E' | 'F';
  value: string;
  numUses: number;
};

export type ManagedContactInput = Pick<
  ManagedContact,
  'name' | 'type' | 'value'
>;

export type ManagedContactsPage = {
  contacts: ManagedContact[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

type ManagedContactResponse = {
  success: boolean;
  contact?: ManagedContact;
  error?: string;
};

export async function fetchAccountSettingsData(): Promise<WebAppAccountSettings> {
  const url = '/api/account/settings';
  const response = await hsFetch(url);
  return assertDataSchemaResponse(response, validateWebAppAccountSettings);
}

export async function fetchWebAppAccount(): Promise<WebAppAccount> {
  const url = '/api/account/user';
  const response = await hsFetch(url);
  return assertDataSchemaResponse(response, validateWebAppAccount);
}

export async function updateEditableIntegration(
  integration: EditableIntegration,
): Promise<string | null> {
  const url = '/account/updateExternalService';

  const body = new FormData();
  body.append('service_type', integration.serviceTypeCode);
  body.append('is_deactivate', integration.isActive ? '1' : '0');
  body.append('csrf_token', getCSRFToken());
  body.append('async', '1');

  const response = await hsFetch(url, {
    method: 'POST',
    body: JSON.stringify(integration),
  });

  // eslint-disable-next-line camelcase
  type MicroSchema = { auth_url?: string };
  const validator = (data: any): data is MicroSchema => {
    if (!data.success) {
      throw new Error('Missing success indicator');
    }
    return true;
  };
  const { auth_url: authUrl } = await assertDataSchemaResponse(
    response,
    validator,
  );

  return authUrl ?? null;
}

export async function uploadCustomLogo(f: File): Promise<string> {
  const formData = new FormData();
  formData.append('customLogoFile', f);
  const url = '/api/account/customLogo';
  const response = await hsFetch(url, {
    method: 'POST',
    body: formData,
  });
  type MicroSchema = { customLogoUrl: string };
  const validator = (data: any): data is MicroSchema => {
    if (!data.customLogoUrl) {
      throw new Error('Missing customLogoUrl');
    }
    return true;
  };
  const { customLogoUrl } = await assertDataSchemaResponse(response, validator);
  return customLogoUrl;
}

export async function saveWebAppAccountSettings(
  settings: WebAppAccountSettings,
): Promise<void> {
  const url = '/api/account/settings';
  const response = await hsFetch(url, {
    method: 'POST',
    body: JSON.stringify(settings),
  });
  assertBasicSuccess(response);
}

export async function saveWebAppAccount(account: WebAppAccount): Promise<void> {
  const url = '/api/account/user';
  const response = await hsFetch(url, {
    method: 'POST',
    body: JSON.stringify(account),
  });
  assertBasicSuccess(response);
}

export async function removeCustomLogo(forTeam: boolean): Promise<void> {
  // This URL doesn't return any data
  const url = `/content/removeCustomLogo${queryParams({
    for_team: forTeam ? '1' : '0',
  })}`;
  await hsFetch(url, {
    method: 'POST',
    body: JSON.stringify({
      csrf_token: getCSRFToken(),
    }),
  });
}

export async function clearAutocomplete() {
  const body = new FormData();
  body.append('clear_autocomplete[_csrf_token]', getCSRFToken());

  await hsFetch('editor/asyncClearAutocomplete', {
    method: 'POST',
    body,
  });

  return true;
}

export async function clearContacts() {
  const body = new FormData();
  body.append('clear_contacts[_csrf_token]', getCSRFToken());

  await hsFetch('editor/asyncClearAutocomplete', {
    method: 'POST',
    body,
  });

  return true;
}

function isManagedContact(value: unknown): value is ManagedContact {
  if (typeof value !== 'object' || value === null) return false;
  const contact = value as Record<string, unknown>;

  return (
    typeof contact.id === 'string' &&
    typeof contact.contactId === 'string' &&
    typeof contact.name === 'string' &&
    (contact.type === 'E' || contact.type === 'F') &&
    typeof contact.value === 'string' &&
    typeof contact.numUses === 'number'
  );
}

export async function getManagedContacts(
  page = 1,
  pageSize = 25,
  search = '',
): Promise<ManagedContactsPage> {
  const response = await hsFetch(
    `/account/getManagedContacts${queryParams({
      page: String(page),
      page_size: String(pageSize),
      search: search.trim() || undefined,
    })}`,
  );
  const data: Record<string, unknown> = await response.json();
  if (!response.ok || !Array.isArray(data.contacts)) {
    throw new Error('We could not load your contacts. Please try again.');
  }
  if (!data.contacts.every(isManagedContact)) {
    throw new Error('The contacts response was invalid.');
  }

  const hasValidPagination =
    Number.isInteger(data.page) &&
    Number.isInteger(data.pageSize) &&
    Number.isInteger(data.totalCount) &&
    Number.isInteger(data.totalPages) &&
    (data.page as number) >= 1 &&
    (data.pageSize as number) >= 1 &&
    (data.totalCount as number) >= 0 &&
    (data.totalPages as number) >= 0;
  if (!hasValidPagination) {
    throw new Error('The contacts pagination response was invalid.');
  }

  return data as ManagedContactsPage;
}

async function mutateManagedContact(
  path: string,
  input: ManagedContactInput,
  guid?: string,
): Promise<ManagedContact> {
  const body = new FormData();
  body.append('csrf_token', getCSRFToken());
  body.append('name', input.name);
  body.append('type', input.type);
  body.append('value', input.value);
  if (guid) body.append('guid', guid);

  const response = await hsFetch(path, { method: 'POST', body });
  const data: ManagedContactResponse = await response.json();
  if (!response.ok || !data.success || !isManagedContact(data.contact)) {
    throw new Error(
      data.error || 'We could not save this contact. Please try again.',
    );
  }

  return data.contact;
}

export async function addManagedContact(
  input: ManagedContactInput,
): Promise<ManagedContact> {
  return mutateManagedContact('/account/addManagedContact', input);
}

export async function updateManagedContact(
  guid: string,
  input: ManagedContactInput,
): Promise<ManagedContact> {
  return mutateManagedContact('/account/updateManagedContact', input, guid);
}

export async function deleteManagedContact(guid: string): Promise<void> {
  const body = new FormData();
  body.append('csrf_token', getCSRFToken());
  body.append('guid', guid);

  const response = await hsFetch('/account/deleteManagedContact', {
    method: 'POST',
    body,
  });
  const data: ManagedContactResponse = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(
      data.error || 'We could not remove this contact. Please try again.',
    );
  }
}

export async function deleteAccount(password: string) {
  const body = new FormData();
  body.append('password', password);

  await hsFetch('/accountSettings/deleteAccount', {
    method: 'POST',
    body,
  });

  return true;
}
